import { Card, Colors, getRelativeTime, Heading, Text } from '@amelio/ui-library';

import { ACTIVITY_STACK_GAP, MARKER_TOP_OFFSET, MEETINGS_TITLE } from './meetingStyles';

import type { MeetingPlanTitles } from '@/pages/ThematicMeetings/utils/meetingTitle';
import type { ThematicMeetingListItem } from '@/types/thematic-meetings';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getMeetingTitle } from '@/pages/ThematicMeetings/utils/meetingTitle';
import { toDateInputLang } from '@/utils/dateUtils';
import { getFullName } from '@/utils/user';

interface RecentActivityProps {
    meetings: ThematicMeetingListItem[];
    planTitles: MeetingPlanTitles;
    isLoading: boolean;
    /** The activity could not be read: the card says so instead of calling itself empty. */
    isError: boolean;
}

const RecentActivityEntries = ({
    meetings,
    planTitles,
    isLoading,
    isError,
}: RecentActivityProps) => {
    const { t } = useAmelioTranslations();
    // `fr_FR`, the language a French-from-France company reads in, is not a locale the library's
    // date helpers know: left as it is, the date comes back in English.
    const dateLanguage = toDateInputLang(useSelectedAppLanguage());

    if (isLoading) {
        return (
            <Skeleton className={'flex flex-col gap-2'}>
                <Bone
                    color={Colors.BASE_100}
                    className={'h-3.5 w-40'}
                />
                <Bone
                    color={Colors.BASE_100}
                    className={'h-3 w-24'}
                />
            </Skeleton>
        );
    }

    if (isError) {
        return (
            <Text color={Colors.BASE_500}>{t('thematicMeetings.list.completedMeetingsError')}</Text>
        );
    }

    if (meetings.length === 0) {
        return (
            <Text color={Colors.BASE_500}>{t('thematicMeetings.list.noCompletedMeetings')}</Text>
        );
    }

    return (
        <ul className={`flex flex-col ${ACTIVITY_STACK_GAP}`}>
            {meetings.map((meeting) => {
                const meetingTitle = getMeetingTitle(meeting, planTitles) ?? '';

                return (
                    /* The marker in a gutter of its own, everything it introduces in one column
                       beside it: name, meeting and elapsed time each own a line, and a long one
                       wraps instead of being clipped. The three lines line up because they share
                       that column, so there is no indent to keep in step with the marker. */
                    <li
                        key={meeting.id}
                        className={'flex items-start gap-1.5'}
                    >
                        {/* `items-start` puts the marker at the top of a now multi-line entry;
                            `MARKER_TOP_OFFSET` brings it back onto the first line. */}
                        <span
                            aria-hidden
                            className={`bg-success ${MARKER_TOP_OFFSET} h-2.5 w-2.5 shrink-0 rounded-full`}
                        />

                        <div className={'flex flex-col gap-1'}>
                            <Text
                                weight={'medium'}
                                color={Colors.BASE_600}
                            >
                                {getFullName(meeting.employee ?? undefined)}
                            </Text>

                            <Text color={Colors.BASE_500}>{meetingTitle}</Text>

                            <Text
                                size={'small'}
                                color={Colors.BASE_500}
                            >
                                {/* `getRelativeTime` reads "from the date to now" ("3 days ago");
                                    `getRelativeTimeToNow` is the mirror image and would say "in 3
                                    days" for a meeting completed three days ago. */}
                                {meeting.completedAt
                                    ? getRelativeTime({
                                          date: meeting.completedAt,
                                          language: dateLanguage,
                                      })
                                    : ''}
                            </Text>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export const RecentActivity = (props: RecentActivityProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Card
            padding={'md'}
            borderRadius={'xl'}
            glass={'sm'}
            dataTestId={'recent-activity-card'}
        >
            {/* Same structure as the sibling QuickStats card — centred title on the glass, entries
                in a white card below it — so the two blocks of the side column read as a pair. */}
            <div className={'flex flex-col gap-4'}>
                <Heading
                    level={4}
                    align={'center'}
                    color={Colors.NAVY}
                    className={MEETINGS_TITLE}
                >
                    {t('thematicMeetings.list.completedMeetings')}
                </Heading>

                <Card
                    variant={'flat'}
                    bgColor={'bg-white/90'}
                    borderRadius={'lg'}
                    dataTestId={'recent-activity-entries'}
                >
                    <RecentActivityEntries {...props} />
                </Card>
            </div>
        </Card>
    );
};
